import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'leaflet/dist/leaflet.css';
import WeeklySchedule from './Components/WeeklySchedule/WeeklySchedule';
import { Container, Row, Col, Toast, Accordion, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import mowerIconImg from './Img/mower-icon.png';
import Logo from './Img/logo.png';

// Anpassad ikon för robotgräsklipparen
const mowerIcon = new L.Icon({
  iconUrl: mowerIconImg,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

// Hjälpfunktion för API-anrop med automatisk JWT-autentisering
const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('jwt');
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Lägg till JWT Bearer token om det finns
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

// Hjälpfunktioner för att läsa roll från JWT-claimen
const ROLE_CLAIM_URI = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

const decodeJwtPayload = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = decodeURIComponent(atob(padded).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    const parsed = JSON.parse(jsonPayload);
    return parsed;
  } catch (e) {
    return null;
  }
};

// Extrahera roll från godtycklig payload (JWT-claims eller API-svar)
const getRoleFromPayload = (payload) => {
  if (!payload) return null;
  const possibleClaims = [
    ROLE_CLAIM_URI,
    'role',
    'roles',
    'cognito:groups',
    'groups'
  ];
  for (const key of possibleClaims) {
    const value = payload[key];
    if (!value) continue;
    if (Array.isArray(value)) {
      const lowered = value.map(v => String(v).toLowerCase());
      const derived = lowered.includes('admin') ? 'admin' : 'user';
      return derived;
    }
    if (typeof value === 'string') {
      const lowered = value.toLowerCase();
      const parts = lowered.split(/[\s,;]+/).filter(Boolean);
      const derived = (parts.includes('admin') || lowered === 'admin') ? 'admin' : 'user';
      return derived;
    }
  }
  return null;
};

const getRoleFromJwt = (token) => {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const role = getRoleFromPayload(payload);
  return role ?? 'user';
};

 



// Beräkna optimal center och zoom för att visa alla positioner
const calculateMapBounds = (positions, currentLat, currentLng) => {
  if (!positions || positions.length === 0) {
    return {
      center: [currentLat, currentLng],
      zoom: 22
    };
  }

  // Inkludera alla positioner plus nuvarande position
  const allPositions = [...positions, { Latitude: currentLat, Longitude: currentLng }];
  
  const lats = allPositions.map(pos => pos.Latitude);
  const lngs = allPositions.map(pos => pos.Longitude);
  
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  
  // Beräkna center
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  
  // Beräkna spridning för att bestämma zoom
  const latSpread = maxLat - minLat;
  const lngSpread = maxLng - minLng;
  const maxSpread = Math.max(latSpread, lngSpread);
  
  // Bestäm zoom baserat på spridning (ju mindre spridning, desto mer zoom)
  let zoom = 22;
  if (maxSpread > 0.001) zoom = 18;
  else if (maxSpread > 0.0005) zoom = 19;
  else if (maxSpread > 0.0002) zoom = 20;
  else if (maxSpread > 0.0001) zoom = 21;
  
  return {
    center: [centerLat, centerLng],
    zoom: zoom
  };
};

function App() {

  // Pre-calc current week number (reserved for future use)
  // const currentWeekNumber = GetWeekNumber(new Date());
  const [showRobotStatus] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMower, setSelectedMower] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedMowerForSettings, setSelectedMowerForSettings] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedTimeInterval, setSelectedTimeInterval] = useState('30m');
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCommandsModal, setShowCommandsModal] = useState(false);
  const [commandsStatus, setCommandsStatus] = useState(null);
  const [commandsLoading, setCommandsLoading] = useState(false);
  const [commandsError, setCommandsError] = useState(null);
  const [showStartOverrideModal, setShowStartOverrideModal] = useState(false);
  const [commandsSubmitting, setCommandsSubmitting] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [userExpandedRows, setUserExpandedRows] = useState(new Set());
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUserIndex, setEditingUserIndex] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', emailAddress: '', mobileNumber: '', notifyOnMowerError: false, role: 'user' });
  const [usersSaving, setUsersSaving] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', emailAddress: '', mobileNumber: '', notifyOnMowerError: false, role: 'user', password: '' });
  const [createUserSaving, setCreateUserSaving] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  // const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState('');
  const [profileFormData, setProfileFormData] = useState({
    name: '',
    emailAddress: '',
    mobileNumber: '',
    notifyOnMowerError: false
  });

  // Schema state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  // "Nu"-tid för röd markör i schema (uppdateras periodiskt)
  const [now, setNow] = useState(new Date());

  // Robot status state
  const [robotStatus, setRobotStatus] = useState(null);
  const [robotLoading, setRobotLoading] = useState(true);
  const [robotError, setRobotError] = useState(null);
  const hasFetchedRef = useRef(false);

  // Kontrollera inloggningsstatus vid sidladdning
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('jwt');
      if (!token) {
        setIsLoggedIn(false);
        setCurrentUserRole(null);
        return;
      }

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/profile', {
        method: 'GET',
      });
      
      if (response.ok) {
        setIsLoggedIn(true);
        let derivedRole = null;
        try {
          const data = await response.json();
          derivedRole = getRoleFromPayload(data);
        } catch (e) {
          // Ignorera JSON-fel och fall tillbaka på JWT
        }
        if (!derivedRole) {
          derivedRole = getRoleFromJwt(token);
        }
        setCurrentUserRole(derivedRole);
      } else {
        // Token är ogiltig, ta bort den
        localStorage.removeItem('jwt');
        setIsLoggedIn(false);
        setCurrentUserRole(null);
      }
    } catch (error) {
      localStorage.removeItem('jwt');
      setIsLoggedIn(false);
      setCurrentUserRole(null);
    }
  };

  // Hämta robot-status data
  const fetchRobotStatus = async () => {
    try {
      setRobotLoading(true);
      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/status', {
        method: 'GET',
      });
      
      const data = await response.json();
      
      if (data.body) {
        const parsedBody = JSON.parse(data.body);
        
        // Konvertera single mower till mowers array för kompatibilitet
        if (parsedBody.mower && !parsedBody.mowers) {
          parsedBody.mowers = [parsedBody.mower];
        }
        setRobotStatus(parsedBody);
      } else {
        setRobotStatus(data);
      }
      setRobotError(null);
    } catch (error) {
      console.error('Error fetching robot status:', error);
      setRobotError('Kunde inte hämta robot-status');
    } finally {
      setRobotLoading(false);
    }
  };

  // Hämta status till Kommandon (cache)
  const fetchCommandsStatus = async () => {
    try {
      setCommandsLoading(true);
      setCommandsError(null);
      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/status', {
        method: 'GET',
      });
      const data = await response.json();
      if (data && data.body) {
        setCommandsStatus(JSON.parse(data.body));
      } else {
        setCommandsStatus(data);
      }
    } catch (e) {
      setCommandsError('Kunde inte hämta status. Försök igen.');
    } finally {
      setCommandsLoading(false);
    }
  };

  // Sänd kommandon
  const postMowerCommand = async (command, durationMinutes) => {
    try {
      setCommandsSubmitting(true);
      const url = durationMinutes != null
        ? `https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/mowercommand?command=${encodeURIComponent(command)}&duration=${encodeURIComponent(durationMinutes)}`
        : `https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/mowercommand?command=${encodeURIComponent(command)}`;
      const resp = await apiRequest(url, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Uppdatera status efter 3 sekunder
      setTimeout(fetchCommandsStatus, 3000);
      setSuccessToastMessage('Kommando skickat');
      setShowSuccessToast(true);
    } catch (e) {
      setCommandsError('Kunde inte skicka kommandot. Försök igen.');
    } finally {
      setCommandsSubmitting(false);
    }
  };

  // Stäng hamburger-meny vid klick utanför
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHamburgerMenu && !event.target.closest('.hamburger-menu-container')) {
        setShowHamburgerMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHamburgerMenu]);

  useEffect(() => {
    // Förhindra dubbla anrop med ref
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let isMounted = true;
    let interval;

    const initData = async () => {
      if (!isMounted) return;
      // Kontrollera inloggningsstatus först
      await checkAuthStatus();
      // Sedan hämta robot status
      await fetchRobotStatus();
    };

    // Första anropet
    initData();
    
    // Sätt upp intervall för uppdateringar
    interval = setInterval(() => {
      if (isMounted) {
        fetchRobotStatus();
      }
    }, 30000);
    
    return () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  // Uppdatera nu-tid var 30:e sekund (för röd markör i schema)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Logga meny- och rollstatus vid förändring
  useEffect(() => {
  }, [currentUserRole, isLoggedIn, showHamburgerMenu]);

  // Manuell refresh
  const handleRefresh = () => {
    fetchRobotStatus();
  };

  // Modal hantering
  const handleShowModal = (mower) => {
    setSelectedMower(mower);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMower(null);
  };

  // Beräkna tidsintervall baserat på val
  const getTimeInterval = (interval) => {
    const now = new Date();
    let minutesAgo;
    
    switch (interval) {
      case '30m':
        minutesAgo = 30;
        break;
      case '3h':
        minutesAgo = 3 * 60;
        break;
      case '12h':
        minutesAgo = 12 * 60;
        break;
      case '24h':
        minutesAgo = 24 * 60;
        break;
      default:
        minutesAgo = 30; // Standard 30 minuter
    }
    
    const pastTime = new Date(now.getTime() - (minutesAgo * 60 * 1000));
    return {
      from: pastTime.toISOString(),
      to: now.toISOString()
    };
  };

  // Hämta historik för valt tidsintervall
  const fetchHistory = async (interval = selectedTimeInterval) => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      
      // Skapa tidsintervall baserat på val
      const { from, to } = getTimeInterval(interval);
      
      const response = await apiRequest(`https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/history?from=${from}&to=${to}`, {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returnerar objekt med items array
        if (data.success && data.items) {
          setHistoryData(data);
        } else {
          setHistoryData({ success: false, items: [], count: 0 });
        }
      } else {
        setHistoryError(`Fel vid hämtning av historik: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Fel vid hämtning av historik:', error);
      setHistoryError('Kunde inte hämta historik. Kontrollera din internetanslutning.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Hämta användare
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/users', {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        let list;
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data.items)) list = data.items;
        else if (Array.isArray(data.users)) list = data.users;
        else list = data ? [data] : [];
        setUsersData(list);
      } else {
        setUsersError(`Fel vid hämtning av användare: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Fel vid hämtning av användare:', error);
      setUsersError('Kunde inte hämta användare. Kontrollera din internetanslutning.');
    } finally {
      setUsersLoading(false);
    }
  };

  // Hämta schema
  const fetchSchedule = async () => {
    try {
      setScheduleLoading(true);
      setScheduleError(null);
      setScheduleData(null);

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/schedule', {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        setScheduleData(data);
      } else {
        setScheduleError(`Fel vid hämtning av schema: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setScheduleError('Kunde inte hämta schema. Kontrollera din internetanslutning.');
    } finally {
      setScheduleLoading(false);
    }
  };

  const toggleUserRowExpansion = (index) => {
    const next = new Set(userExpandedRows);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setUserExpandedRows(next);
  };

  const openEditUserModal = (user, index) => {
    setEditingUserIndex(index);
    setEditUserForm({
      name: user.name ?? user.Name ?? '',
      emailAddress: user.emailAddress ?? user.email ?? user.username ?? '',
      mobileNumber: user.mobileNumber ?? user.phone ?? '',
      notifyOnMowerError: Boolean(user.notifyOnMowerError),
      role: user.role ?? 'user'
    });
    setShowEditUserModal(true);
  };

  const closeEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUserIndex(null);
    setEditUserForm({ name: '', emailAddress: '', mobileNumber: '', notifyOnMowerError: false, role: 'user' });
  };

  const handleEditUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUserForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveEditedUser = async () => {
    try {
      setUsersSaving(true);
      setUsersError(null);
      const current = usersData[editingUserIndex] || {};
      const idValue = current.UserId ?? current.userId ?? current.id;
      if (!idValue) {
        throw new Error('UserId saknas på vald användare');
      }
      const payload = {
        UserId: idValue,
        emailAddress: editUserForm.emailAddress,
        name: editUserForm.name,
        mobileNumber: editUserForm.mobileNumber,
        notifyOnMowerError: editUserForm.notifyOnMowerError,
        role: editUserForm.role,
      };

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/users', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      // Uppdatera lokalt i tabellen efter lyckat svar
      setUsersData(prev => prev.map((u, i) => {
        if (i !== editingUserIndex) return u;
        return {
          ...u,
          name: editUserForm.name,
          Name: editUserForm.name,
          emailAddress: editUserForm.emailAddress,
          email: editUserForm.emailAddress,
          mobileNumber: editUserForm.mobileNumber,
          notifyOnMowerError: editUserForm.notifyOnMowerError,
          role: editUserForm.role,
        };
      }));
      closeEditUserModal();
      setSuccessToastMessage('Användaren uppdaterades');
      setShowSuccessToast(true);
    } catch (err) {
      console.error('Kunde inte spara användare:', err);
      setUsersError(`Kunde inte spara användare: ${err.message}`);
    } finally {
      setUsersSaving(false);
    }
  };

  const handleDeleteUser = async (index) => {
    const confirmed = window.confirm('Vill du ta bort den här användaren?');
    if (!confirmed) return;
    setUsersData(prev => prev.filter((_, i) => i !== index));
  };

  // Settings modal hantering
  const handleShowSettingsModal = async (mower) => {
    setSelectedMowerForSettings(mower);
    setShowSettingsModal(true);
    
    // Hämta historik när modalen öppnas
    await fetchHistory();
  };

  // Hantera expandering av historik-rader
  const toggleRowExpansion = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  // Hantera ändring av tidsintervall
  const handleTimeIntervalChange = (e) => {
    const newInterval = e.target.value;
    setSelectedTimeInterval(newInterval);
    setExpandedRows(new Set()); // Rensa expanderade rader
    fetchHistory(newInterval); // Hämta ny data
  };

  // Hamburger-meny hantering
  const toggleHamburgerMenu = () => {
    const next = !showHamburgerMenu;
    setShowHamburgerMenu(next);
  };

  const handleHamburgerMenuClick = (action) => {
    setShowHamburgerMenu(false); // Stäng menyn först
    
    switch (action) {
      case 'refresh':
        handleRefresh();
        break;
      case 'users':
        setShowUsersModal(true);
        fetchUsers();
        break;
      case 'info':
        handleShowModal(robotStatus?.mowers?.[0]);
        break;
      case 'commands':
        setShowCommandsModal(true);
        fetchCommandsStatus();
        break;
      case 'schedule':
        setShowScheduleModal(true);
        fetchSchedule();
        break;
      case 'settings':
        handleShowSettingsModal(robotStatus?.mowers?.[0]);
        break;
      case 'profile':
        handleShowProfileModal();
        break;
      case 'logout':
        localStorage.removeItem('jwt');
        setIsLoggedIn(false);
        setCurrentUserRole(null);
        setSuccessToastMessage('Utloggad');
        setShowSuccessToast(true);
        break;
      default:
        break;
    }
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setScheduleError(null);
  };

  const handleCloseSettingsModal = () => {
    setShowSettingsModal(false);
    setSelectedMowerForSettings(null);
    setHistoryData(null);
    setHistoryError(null);
    setExpandedRows(new Set()); // Rensa expanderade rader
  };

  // Profile modal hantering
  const handleShowProfileModal = async () => {
    setShowProfileModal(true);
    setProfileLoading(true);
    setProfileError(null);
    // setProfileData(null);

    try {
      const token = localStorage.getItem('jwt');
      if (!token) {
        setProfileError('Ingen JWT-token hittades');
        setProfileLoading(false);
        return;
      }

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/profile', {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        const roleFromProfile = getRoleFromPayload(data);
        setCurrentUserRole(roleFromProfile ?? getRoleFromJwt(localStorage.getItem('jwt')));
        // Fyll formuläret med data från API:et
        setProfileFormData({
          name: data.name || '',
          emailAddress: data.emailAddress || data.username || '',
          mobileNumber: data.mobileNumber || '',
          notifyOnMowerError: data.notifyOnMowerError || false
        });
      } else {
        setProfileError(`Fel vid hämtning: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Fel vid hämtning av profil:', error);
      setProfileError(`Nätverksfel: ${error.message}`);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    setProfileError(null);
    setProfileFormData({
      name: '',
      emailAddress: '',
      mobileNumber: '',
      notifyOnMowerError: false
    });
  };

  const handleProfileFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProfileFormSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError(null);

    try {
      const payload = {
        emailAddress: profileFormData.emailAddress,
        password: loginData.password, // Använder lösenordet från inloggningsdata
        name: profileFormData.name,
        mobileNumber: profileFormData.mobileNumber,
        notifyOnMowerError: profileFormData.notifyOnMowerError
      };

      const token = localStorage.getItem('jwt');
      if (!token) {
        setProfileError('Ingen JWT-token hittades');
        return;
      }

      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/users', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      
      // Stäng modal vid framgång
      setShowProfileModal(false);
      // Visa framgångsmeddelande
      setSuccessToastMessage('Profiluppgifter sparades');
      setShowSuccessToast(true);
      
    } catch (error) {
      console.error('Fel vid sparande av profil:', error);
      setProfileError('Kunde inte spara profilen. Försök igen.');
    } finally {
      setProfileSaving(false);
    }
  };

  // Login modal hantering
  const handleShowLoginModal = () => {
    setShowLoginModal(true);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
    setLoginData({ username: '', password: '' });
    setLoginError('');
    setLoginLoading(false);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          username: loginData.username,
          password: loginData.password
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Spara JWT token i localStorage
        if (data.token) {
          localStorage.setItem('jwt', data.token);
          setIsLoggedIn(true);
          const roleAfterLogin = getRoleFromJwt(data.token);
          setCurrentUserRole(roleAfterLogin);
          handleCloseLoginModal();
        } else {
          setLoginError('Ingen token mottagen från servern.');
        }
      } else {
        // Inloggning misslyckades
        setLoginError('Felaktigt användarnamn eller lösenord. Försök igen.');
        console.log('Inloggning misslyckades:', response.status);
      }
    } catch (error) {
      console.error('Fel vid inloggning:', error);
      setLoginError('Ett fel uppstod vid inloggning. Försök igen.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  // Översätt status till svenska
  const translateStatus = (status) => {
    const translations = {
      'RESTRICTED': 'Begränsad',
      'PARKED_IN_CS': 'Parkerad i laddstation',
      'MAIN_AREA': 'Huvudområde',
      'IN_OPERATION': 'I drift',
      'OK': 'OK',
      'ERROR': 'Fel',
      'FATAL_ERROR': 'FEL',
      'PAUSED': 'Pausad',
      'MOWING': 'Klipper',
      'GOING_HOME': 'På väg hem',
      'CHARGING': 'Laddar',
      'LEAVING': 'Lämnar',
      'WORKING': 'Arbetar',
      'SEARCHING': 'Söker',
      'MANUAL': 'Manuell',
      'WEEK_SCHEDULE': 'Veckans schema',
      'NONE': 'Ingen',
      'NOT_APPLICABLE': 'Ingen aktivitet'
    };
    return translations[status] || status;
  };

  // Formatera status med ikoner
  const formatStatusWithIcon = (status) => {
    const translated = translateStatus(status);
    if (status === 'CHARGING') {
      return (
        <>
          <i className="fas fa-bolt charging-static-icon"></i>
          {translated}
          <span className="charging-dots"></span>
        </>
      );
    }
    if (status === 'GOING_HOME') {
      return (
        <>
          {translated}
          <span className="going-home-dots"></span>
        </>
      );
    }
    return translated;
  };

  // Få batteriklass baserat på nivå
  const getBatteryClass = (percentage) => {
    if (percentage > 75) return 'battery-high';
    if (percentage > 50) return 'battery-medium';
    if (percentage > 25) return 'battery-low';
    return 'battery-critical';
  };

  // Få statusklass för färgkodning
  const getStatusClass = (status) => {
    const statusClasses = {
      'OK': 'status-ok',
      'IN_OPERATION': 'status-ok',
      'MOWING': 'status-working',
      'WORKING': 'status-working',
      'CHARGING': 'status-charging',
      'PARKED_IN_CS': 'status-charging',
      'ERROR': 'status-error',
      'FATAL_ERROR': 'status-error',
      'RESTRICTED': 'status-warning',
      'PAUSED': 'status-warning'
    };
    return statusClasses[status] || 'status-default';
  };

  // Översätt felkoder till beskrivande text
  const getErrorDescription = (errorCode) => {
    const errorDescriptions = {
      0: 'Inget fel',
      1: 'Fångad av säkerhetsfunktion',
      2: 'Stoppad av säkerhetsfunktion',
      3: 'Lyft upp',
      4: 'Lutad',
      5: 'Hjul motor blockerad höger',
      6: 'Hjul motor blockerad vänster',
      7: 'Hjul motor överbelastad höger',
      8: 'Hjul motor överbelastad vänster',
      9: 'Klippmotor blockerad',
      10: 'Klippmotor överbelastad',
      11: 'Slumpmässig kollision sensor',
      12: 'Kollision sensor defekt',
      13: 'Ingen slinga signal',
      14: 'Svag slinga signal',
      15: 'Slinga sensor A defekt',
      16: 'Slinga sensor B defekt',
      17: 'Slinga sensor C defekt',
      18: 'Slinga sensor defekt',
      19: 'Oväntat slinga meddelande',
      20: 'Minnesproblem',
      21: 'Lutningssensor defekt',
      22: 'Laddningsfel',
      23: 'Batterifel',
      24: 'Läsfel på batteritemperatur',
      25: 'Alarmfel',
      26: 'Klippmotor höjd problem',
      27: 'Elektronisk problem',
      28: 'Elektronisk problem',
      29: 'Internt kommunikationsfel',
      30: 'Fel utomhustemperatur'
    };
    return errorDescriptions[errorCode] || `Okänt fel (kod: ${errorCode})`;
  };

  return (
    <div className="App" style={{alignItems: "center"}}>

      
            {/* Robot-status ruta */}
      {showRobotStatus && (
        <div className="RobotStatusContainer">
          <div className="robot-status-actions" style={{position: 'absolute', top: 'calc(0.375rem + 10px)', right: 'calc(0.375rem + 10px)', zIndex: 1030}}>
            {isLoggedIn ? (
              <div className="hamburger-menu-container" style={{ position: 'relative', zIndex: 1030 }}>
                <i 
                  className="fas fa-bars"
                  onClick={toggleHamburgerMenu}
                  title="Meny"
                  style={{ fontSize: '20px', color: '#666', cursor: 'pointer', transition: 'color 0.2s ease' }}
                  onMouseOver={(e) => e.target.style.color = '#007bff'}
                  onMouseOut={(e) => e.target.style.color = '#666'}
            ></i>
                
                {showHamburgerMenu && (
                  <>
                    {/* Invisible backdrop to catch clicks */}
                    <div 
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1029
                      }}
                      onClick={() => setShowHamburgerMenu(false)}
                    />
                    
                    {/* The actual menu */}
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      // Align menu's right edge with the right edge of RobotStatusContainer
                      right: 'calc(-1 * (0.375rem + 10px))',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      zIndex: 1030,
                      minWidth: '180px'
                    }}>
                    <div 
                      onClick={() => handleHamburgerMenuClick('refresh')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-sync-alt" style={{ width: '16px', color: '#666' }}></i>
                      Uppdatera
                    </div>
                    {currentUserRole === 'admin' && (
                      <div 
                        onClick={() => handleHamburgerMenuClick('users')}
                        style={{
                          padding: '10px 15px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f8f9fa',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <i className="fas fa-users" style={{ width: '16px', color: '#666' }}></i>
                        Användare
                      </div>
                    )}
                    <div 
                      onClick={() => handleHamburgerMenuClick('info')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-info-circle" style={{ width: '16px', color: '#666' }}></i>
                      Info
                    </div>
                    <div 
                      onClick={() => handleHamburgerMenuClick('commands')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-terminal" style={{ width: '16px', color: '#666' }}></i>
                      Kommandon
                    </div>
                    <div 
                      onClick={() => handleHamburgerMenuClick('schedule')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-calendar-alt" style={{ width: '16px', color: '#666' }}></i>
                      Schema
                    </div>
                    <div 
                      onClick={() => handleHamburgerMenuClick('settings')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-clock-rotate-left" style={{ width: '16px', color: '#666' }}></i>
                      Historik
                    </div>
                    <div 
                      onClick={() => handleHamburgerMenuClick('profile')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f8f9fa',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-user" style={{ width: '16px', color: '#666' }}></i>
                      Profil
                    </div>
                    {/* Separator above logout */}
                    <div style={{ height: '1px', backgroundColor: '#f1f3f5' }}></div>
                    <div 
                      onClick={() => handleHamburgerMenuClick('logout')}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-sign-out-alt" style={{ width: '16px', color: '#666' }}></i>
                      Logga ut
                    </div>
                  </div>
              </>
            )}
              </div>
            ) : (
              <i 
                className="fas fa-lock lock-icon"
                onClick={handleShowLoginModal}
                title="Logga in"
            ></i>
            )}
          </div>
          <Container>
            <Row>
              <Col>
                <div className="robot-status-card">
                  {robotLoading ? (
                    <div className="robot-loading">
                      <Spinner animation="border" size="sm" />
                      <span className="loading-text">Hämtar status...</span>
                    </div>
                  ) : robotError ? (
                    <Alert variant="danger" className="robot-error">{robotError}</Alert>
                  ) : robotStatus && robotStatus.mowers && robotStatus.mowers.length > 0 ? (
                    <div className="robot-grid">
                      {robotStatus.mowers.map((mower, index) => (
                        <div key={mower.Id} className="mower-card">
                          <div className="mower-header">
                            <div className="mower-name">
                              <div className="name-with-actions">
                                <div className="name">{mower.Name}</div>
                                <div className="battery-container">
                                  <div className={`battery-icon ${getBatteryClass(mower.BatteryPercent)}`}>
                                    <div className="battery-level" style={{width: `${mower.BatteryPercent}%`}}></div>
                                    <div className="battery-percentage">
                                      {(mower.Activity === 'CHARGING' || mower.Activity === 'PARKED_IN_CS') && mower.BatteryPercent < 100 && (
                                        <i className="fas fa-bolt charging-icon"></i>
                                      )}
                                      {mower.BatteryPercent}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="model">{mower.Model}</div>
                            </div>
                          </div>
                          
                                                     <div className="mower-content-row">
                             <div className="status-column">
                               <div className="status-item">
                                 <span className="status-label">Status:</span>
                                 <span className={`status-value ${getStatusClass(mower.Status)}`}>
                                   {translateStatus(mower.Status)}
                                 </span>
                               </div>
                               <div className="status-item">
                                 <span className="status-label">Aktivitet:</span>
                                 <span className={`status-value ${getStatusClass(mower.Activity)}`}>
                                   {formatStatusWithIcon(mower.Activity)}
                                 </span>
                               </div>
                             </div>
                             
                             <div className="map-column">
                                                             <div className="mini-map">
                                {mower.Latitude && mower.Longitude ? (() => {
                                  const mapBounds = calculateMapBounds(mower.Positions, mower.Latitude, mower.Longitude);
                                  return (
                                    <MapContainer
                                      center={mapBounds.center}
                                      zoom={mapBounds.zoom}
                                      style={{ height: '120px', width: '100%' }}
                                      zoomControl={false}
                                      dragging={false}
                                      touchZoom={false}
                                      doubleClickZoom={false}
                                      scrollWheelZoom={false}
                                      boxZoom={false}
                                      keyboard={false}
                                      attributionControl={true}
                                    >
                                      <TileLayer
                                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                        attribution='&copy; Esri &copy; Maxar'
                                      />
                                      
                                      {/* Rörelsemönster på förstasidan */}
                                      {mower.Positions && mower.Positions.length > 0 && (
                                        <Polyline
                                          positions={mower.Positions.map(pos => [pos.Latitude, pos.Longitude])}
                                          pathOptions={{
                                            color: '#007bff',
                                            weight: 2,
                                            opacity: 0.7,
                                            dashArray: '3, 3'
                                          }}
                                        />
                                      )}
                                      
                                      <Marker 
                                        position={[mower.Latitude, mower.Longitude]}
                                        icon={mowerIcon}
                                      />
                                    </MapContainer>
                                  );
                                })() : (
                                   <div className="map-placeholder">
                                     <span>🗺️</span>
                                     <p>Väntar på position...</p>
                                   </div>
                                 )}
                               </div>
                             </div>
                           </div>
                          
                          {mower.HasError && (
                            <Alert variant="warning" className="mower-error">
                              <strong>⚠️ Fel:</strong> {getErrorDescription(mower.ErrorCode)}
                              {mower.ErrorMessage && mower.ErrorMessage !== '' && (
                                <div style={{ fontSize: '0.9em', marginTop: '4px', opacity: 0.8 }}>
                                  Detaljer: {mower.ErrorMessage}
                                </div>
                              )}
                            </Alert>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data">
                      <span className="robot-icon">🤖</span>
                      <p>Ingen robot-status data tillgänglig</p>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}

      {/* Modal för detaljerad robot-info */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Detaljerad information - {selectedMower?.Name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMower && (
            <div className="modal-content-grid">
              <div className="info-section">
                <h5>🤖 Grundläggande information</h5>
                <div className="info-grid">
                  <div><strong>Namn:</strong> {selectedMower.Name}</div>
                  <div><strong>Modell:</strong> {selectedMower.Model}</div>
                </div>
              </div>

              <div className="info-section">
                <h5>📊 Status & Aktivitet</h5>
                <div className="info-grid">
                  <div><strong>Status:</strong> <span className={`inline-status ${getStatusClass(selectedMower.Status)}`}>{translateStatus(selectedMower.Status)}</span></div>
                  <div><strong>Aktivitet:</strong> <span className={`inline-status ${getStatusClass(selectedMower.Activity)}`}>{formatStatusWithIcon(selectedMower.Activity)}</span></div>
                  <div><strong>Läge:</strong> {translateStatus(selectedMower.Mode)}</div>
                  <div><strong>Begränsningsorsak:</strong> {translateStatus(selectedMower.RestrictedReason || 'NONE')}</div>
                </div>
              </div>

              <div className="info-section">
                <h5>🔋 Batteri & Energi</h5>
                <div className="info-grid">
                  <div><strong>Batterinivå:</strong> {selectedMower.BatteryPercent}%</div>
                  <div><strong>Laddningscykler:</strong> {selectedMower.ChargingCycles?.toLocaleString('sv-SE') || 'N/A'}</div>
                  <div><strong>Total laddningstid:</strong> {selectedMower.TotalChargingTime || 'N/A'}</div>
                </div>
              </div>

              <div className="info-section">
                <h5>📍 Position & Rörelse</h5>
                <div className="info-grid">
                  <div><strong>Total körsträcka:</strong> {selectedMower.TotalDriveDistance?.toLocaleString('sv-SE')} m</div>
                  <div><strong>Kollisioner:</strong> {selectedMower.Collisions?.toLocaleString('sv-SE')}</div>
                </div>
                
                {selectedMower.Positions && selectedMower.Positions.length > 0 && (
                  <div className="movement-map-section">
                    <h6 style={{marginTop: '20px', marginBottom: '15px', color: '#495057'}}>
                      🗺️ Rörelsemönster
                    </h6>
                    <div className="movement-map">
                      <MapContainer
                        center={[selectedMower.Latitude, selectedMower.Longitude]}
                        zoom={19}
                        style={{ height: '300px', width: '100%', borderRadius: '8px' }}
                        zoomControl={true}
                        dragging={true}
                        touchZoom={true}
                        doubleClickZoom={true}
                        scrollWheelZoom={true}
                        boxZoom={true}
                        keyboard={true}
                      >
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          attribution='&copy; Esri &copy; Maxar'
                        />
                        
                        {/* Rörelsemönster som streck */}
                        <Polyline
                          positions={selectedMower.Positions.map(pos => [pos.Latitude, pos.Longitude])}
                          pathOptions={{
                            color: '#007bff',
                            weight: 3,
                            opacity: 0.8,
                            dashArray: '5, 5'
                          }}
                        />
                        
                        {/* Startposition (grön) */}
                        <Marker 
                          position={[selectedMower.Positions[0].Latitude, selectedMower.Positions[0].Longitude]}
                          icon={new L.Icon({
                            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
                            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                            iconSize: [25, 41],
                            iconAnchor: [12, 41],
                            popupAnchor: [1, -34],
                            shadowSize: [41, 41]
                          })}
                        />
                        
                        {/* Slutposition (röd) */}
                        <Marker 
                          position={[selectedMower.Positions[selectedMower.Positions.length - 1].Latitude, selectedMower.Positions[selectedMower.Positions.length - 1].Longitude]}
                          icon={new L.Icon({
                            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                            iconSize: [25, 41],
                            iconAnchor: [12, 41],
                            popupAnchor: [1, -34],
                            shadowSize: [41, 41]
                          })}
                        />
                        
                        {/* Aktuell position (robot-ikon) */}
                        <Marker 
                          position={[selectedMower.Latitude, selectedMower.Longitude]}
                          icon={new L.Icon({
                            iconUrl: mowerIconImg,
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                            popupAnchor: [0, -16],
                          })}
                        />
                      </MapContainer>
                    </div>
                    <div className="map-legend">
                      <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#28a745'}}></span>
                        <span>Startposition</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#007bff'}}></span>
                        <span>Rörelsemönster</span>
                      </div>
                      <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#dc3545'}}></span>
                        <span>Slutposition</span>
                      </div>
                      <div className="legend-item">
                        <img src={mowerIconImg} alt="Robot" style={{width: '16px', height: '16px'}} />
                        <span>Aktuell position</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="info-section">
                <h5>⏱️ Arbetstider</h5>
                <div className="info-grid">
                  <div><strong>Total klipptid:</strong> {selectedMower.TotalCuttingTime || 'N/A'}</div>
                  <div><strong>Total körtid:</strong> {selectedMower.TotalRunningTime || 'N/A'}</div>
                  <div><strong>Total söktid:</strong> {selectedMower.TotalSearchingTime || 'N/A'}</div>
                  <div><strong>Nästa start:</strong> {selectedMower.NextStartTime ? new Date(selectedMower.NextStartTime).toLocaleString('sv-SE') : 'N/A'}</div>
                </div>
              </div>

              <div className="info-section">
                <h5>⚙️ Inställningar</h5>
                <div className="info-grid">
                  <div><strong>Klipphöjd:</strong> {selectedMower.CuttingHeight} mm</div>
                  <div><strong>Strålkastarläge:</strong> {translateStatus(selectedMower.HeadlightMode || 'N/A')}</div>
                  <div><strong>Ansluten:</strong> {selectedMower.Connected ? '✅ Ja' : '❌ Nej'}</div>
                </div>
              </div>

              {selectedMower.HasError && (
                <div className="info-section error-section">
                  <h5>⚠️ Fel</h5>
                  <div className="error-details">
                    <div><strong>Felbeskrivning:</strong> {getErrorDescription(selectedMower.ErrorCode)}</div>
                    <div><strong>Felkod:</strong> {selectedMower.ErrorCode}</div>
                    {selectedMower.ErrorMessage && selectedMower.ErrorMessage !== '' && (
                      <div><strong>Detaljer:</strong> {selectedMower.ErrorMessage}</div>
                    )}
                    <div><strong>Kan bekräfta fel:</strong> {selectedMower.CanConfirmError ? 'Ja' : 'Nej'}</div>
                  </div>
                </div>
              )}


            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Stäng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Schedule Modal */}
      <Modal show={showScheduleModal} onHide={handleCloseScheduleModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-alt"></i> Klipparens schema{scheduleData?.name ? ` - ${scheduleData.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduleLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Hämtar schema...
            </div>
          ) : scheduleError ? (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle"></i> {scheduleError}
            </Alert>
          ) : scheduleData && Array.isArray(scheduleData.schedule) && scheduleData.schedule.length > 0 ? (
            <div>
              {(() => {
                const days = [
                  { key: 'monday', label: 'Måndag' },
                  { key: 'tuesday', label: 'Tisdag' },
                  { key: 'wednesday', label: 'Onsdag' },
                  { key: 'thursday', label: 'Torsdag' },
                  { key: 'friday', label: 'Fredag' },
                  { key: 'saturday', label: 'Lördag' },
                  { key: 'sunday', label: 'Söndag' },
                ];
                const toMinutes = (hhmmss) => {
                  const [hh, mm] = hhmmss.split(':').map(Number);
                  return (hh * 60) + (mm || 0);
                };
                const formatHM = (m) => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).toString().padStart(2,'0')}`;
                const nowDayIndex = new Date(now).getDay(); // 0=Sun..6=Sat
                const nowMinutes = (new Date(now).getHours() * 60) + new Date(now).getMinutes();
                const mapDayToIndex = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
                return (
                  <div>
                    {days.map(day => {
                      const slots = scheduleData.schedule
                        .filter(s => s[day.key])
                        .map(s => {
                          const start = toMinutes(s.startTime);
                          const end = Math.min(start + (s.durationMinutes || 0), 24 * 60);
                          return { start, end };
                        });
                      const isToday = mapDayToIndex[day.key] === nowDayIndex;
                      const nowLeftPct = (nowMinutes / (24 * 60)) * 100;
                      return (
                        <div key={day.key} style={{ marginBottom: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '80px', color: '#495057', fontWeight: 600 }}>{day.label}</div>
                            <div style={{ position: 'relative', height: '22px', background: '#f1f3f5', borderRadius: '11px', flex: 1 }}>
                              {[0, 25, 50, 75, 100].map((pct, i) => (
                                <div key={i} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: '1px', background: 'rgba(0,0,0,0.06)' }}></div>
                              ))}
                              {slots.map((slot, i) => {
                                const left = (slot.start / (24 * 60)) * 100;
                                const width = Math.max(0, Math.min(((slot.end - slot.start) / (24 * 60)) * 100, 100 - left));
                                return (
                                  <div key={i} style={{ position: 'absolute', left: `${left}%`, width: `${width}%`, top: 0, bottom: 0, background: '#0d6efd', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ color: 'white', fontSize: '11px', fontWeight: 600 }}>{formatHM(slot.start)}–{formatHM(slot.end)}</span>
                                  </div>
                                );
                              })}
                              {isToday && (
                                <div style={{ position: 'absolute', left: `${nowLeftPct}%`, top: -2, bottom: -2, width: '2px', background: '#dc3545', boxShadow: '0 0 0 1px rgba(220,53,69,0.25)' }} title={`Nu: ${formatHM(nowMinutes)}`}></div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : (
            <Alert variant="info">Inget schema hittades.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseScheduleModal}>
            Stäng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Commands Modal */}
      <Modal show={showCommandsModal} onHide={() => setShowCommandsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-terminal"></i> Kommandon
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {commandsLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Hämtar status...
            </div>
          ) : commandsError ? (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle"></i> {commandsError}
            </Alert>
          ) : commandsStatus ? (
            <div>
              {(() => {
                const mower = commandsStatus?.mower || (commandsStatus?.mowers && commandsStatus.mowers[0]) || {};
                const battery = Math.max(0, Math.min(100, Number(mower.BatteryPercent) || 0));
                return (
                  <div className="mower-card" style={{ marginBottom: '20px' }}>
                    <div className="mower-header">
                      <div className="mower-name">
                        <div className="name-with-actions">
                          <div className="name">{mower.Name}</div>
                          <div className="battery-container">
                            <div className={`battery-icon ${getBatteryClass(battery)}`}>
                              <div className="battery-level" style={{width: `${battery}%`}}></div>
                              <div className="battery-percentage">
                                {(mower.Activity === 'CHARGING' || mower.Activity === 'PARKED_IN_CS') && battery < 100 && (
                                  <i className="fas fa-bolt charging-icon"></i>
                                )}
                                {battery}%
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="model">{mower.Model}</div>
                      </div>
                    </div>
                    <div className="mower-content-row">
                      <div className="status-column">
                        <div className="status-item">
                          <span className="status-label">Status:</span>
                          <span className={`status-value ${getStatusClass(mower.Status)}`}>{translateStatus(mower.Status)}</span>
                        </div>
                        <div className="status-item">
                          <span className="status-label">Aktivitet:</span>
                          <span className={`status-value ${getStatusClass(mower.Activity)}`}>{formatStatusWithIcon(mower.Activity)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="d-flex gap-2" style={{ marginTop: '20px' }}>
                <Button variant="success" disabled={commandsSubmitting} onClick={() => setShowStartOverrideModal(true)}>
                  <i className="fas fa-play me-1"></i> Start
                </Button>
                <Button variant="danger" disabled={commandsSubmitting} onClick={() => postMowerCommand('stop')}>
                  <i className="fas fa-stop me-1"></i> Stopp
                </Button>
                <Button variant="outline-secondary" disabled={commandsSubmitting} onClick={fetchCommandsStatus}>
                  <i className="fas fa-sync-alt me-1"></i> Uppdatera
                </Button>
              </div>
            </div>
          ) : (
            <Alert variant="info">Ingen status tillgänglig.</Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Start override modal */}
      <Modal show={showStartOverrideModal} onHide={() => setShowStartOverrideModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Starta – undantag från schema
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Hur många minuter vill du undanhålla från standardschemat?</p>
          <div className="d-flex gap-2">
            <Button variant="primary" disabled={commandsSubmitting} onClick={() => { setShowStartOverrideModal(false); postMowerCommand('start', 60); }}>1 timme</Button>
            <Button variant="primary" disabled={commandsSubmitting} onClick={() => { setShowStartOverrideModal(false); postMowerCommand('start', 180); }}>3 timmar</Button>
            <Button variant="primary" disabled={commandsSubmitting} onClick={() => { setShowStartOverrideModal(false); postMowerCommand('start', 360); }}>6 timmar</Button>
          </div>
        </Modal.Body>
      </Modal>
      {/* Create User Modal */}
      <Modal show={showCreateUserModal} onHide={() => setShowCreateUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-plus"></i> Skapa ny användare
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createUserError && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle"></i> {createUserError}
            </Alert>
          )}
          <Form onSubmit={async (e) => {
            e.preventDefault();
            try {
              setCreateUserSaving(true);
              setCreateUserError(null);
              const payload = {
                emailAddress: createUserForm.emailAddress,
                name: createUserForm.name,
                mobileNumber: createUserForm.mobileNumber,
                notifyOnMowerError: createUserForm.notifyOnMowerError,
                role: createUserForm.role,
                password: createUserForm.password || undefined,
              };
              const response = await apiRequest('https://59sp36vvk5.execute-api.eu-north-1.amazonaws.com/Production/obk_mower/users', {
                method: 'POST',
                body: JSON.stringify(payload),
              });
              if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
              // Uppdatera listan lokalt: lägg till överst
              const json = await response.json().catch(() => ({}));
              const created = (json && (json.user || json)) || payload;
              setUsersData(prev => [created, ...prev]);
              setShowCreateUserModal(false);
              setCreateUserForm({ name: '', emailAddress: '', mobileNumber: '', notifyOnMowerError: false, role: 'user', password: '' });
              setSuccessToastMessage('Ny användare skapad');
              setShowSuccessToast(true);
            } catch (err) {
              console.error('Kunde inte skapa användare:', err);
              setCreateUserError(`Kunde inte skapa användare: ${err.message}`);
            } finally {
              setCreateUserSaving(false);
            }
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Roll</Form.Label>
              <Form.Select name="role" value={createUserForm.role} onChange={(e) => setCreateUserForm(prev => ({ ...prev, role: e.target.value }))}>
                <option value="user">Användare</option>
                <option value="admin">Administratör</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Namn</Form.Label>
              <Form.Control name="name" value={createUserForm.name} onChange={(e) => setCreateUserForm(prev => ({ ...prev, name: e.target.value }))} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>E‑postadress</Form.Label>
              <Form.Control type="email" name="emailAddress" value={createUserForm.emailAddress} onChange={(e) => setCreateUserForm(prev => ({ ...prev, emailAddress: e.target.value }))} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lösenord</Form.Label>
              <Form.Control type="password" name="password" value={createUserForm.password} onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobilnummer</Form.Label>
              <Form.Control name="mobileNumber" value={createUserForm.mobileNumber} onChange={(e) => setCreateUserForm(prev => ({ ...prev, mobileNumber: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check type="checkbox" label="Meddela vid robotfel" checked={createUserForm.notifyOnMowerError} onChange={(e) => setCreateUserForm(prev => ({ ...prev, notifyOnMowerError: e.target.checked }))} />
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={() => setShowCreateUserModal(false)} disabled={createUserSaving}>Avbryt</Button>
              <Button variant="primary" type="submit" disabled={createUserSaving}>
                {createUserSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Skapar...
                  </>
                ) : (
                  'Skapa'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      {/* Users Modal */}
      <Modal show={showUsersModal} onHide={() => setShowUsersModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-users"></i> Användare
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <Button size="sm" variant="primary" onClick={() => setShowCreateUserModal(true)}>
              <i className="fas fa-user-plus me-1"></i> Skapa ny
            </Button>
          </div>
          {usersLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Hämtar användare...
            </div>
          ) : usersError ? (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle"></i> {usersError}
            </Alert>
          ) : usersData && usersData.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th className="d-none d-sm-table-cell">Namn</th>
                    <th>E‑postadress</th>
                    <th>Roll</th>
                    <th style={{ width: '110px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {usersData.map((user, idx) => (
                    <React.Fragment key={idx}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => toggleUserRowExpansion(idx)}>
                        <td>
                          <i className={`fas fa-chevron-${userExpandedRows.has(idx) ? 'down' : 'right'}`} style={{ color: '#666', fontSize: '12px' }}></i>
                        </td>
                        <td className="d-none d-sm-table-cell">{user.name ?? user.Name ?? ''}</td>
                        <td>{user.emailAddress ?? user.email ?? user.username ?? ''}</td>
                        <td>{(user.role ?? 'user') === 'admin' ? 'admin' : 'Anv'}</td>
                        <td className="text-end" onClick={(e) => e.stopPropagation()}>
                          <Button variant="link" size="sm" title="Ändra" onClick={() => openEditUserModal(user, idx)}>
                            <i className="fas fa-pen"></i>
                          </Button>
                          <Button variant="link" size="sm" title="Ta bort" onClick={() => handleDeleteUser(idx)}>
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                      {userExpandedRows.has(idx) && (
                        <tr>
                          <td colSpan="5" style={{ backgroundColor: '#f8f9fa', padding: '12px' }}>
                            <pre style={{
                              fontSize: '11px',
                              margin: 0,
                              backgroundColor: '#ffffff',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              padding: '10px',
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}>
                              {JSON.stringify(user, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert variant="info">Inga användare hittades.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUsersModal(false)}>
            Stäng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditUserModal} onHide={closeEditUserModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-pen"></i> Ändra användare
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => { e.preventDefault(); saveEditedUser(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Roll</Form.Label>
              <Form.Select name="role" value={editUserForm.role} onChange={handleEditUserFormChange}>
                <option value="user">Användare</option>
                <option value="admin">Administratör</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Namn</Form.Label>
              <Form.Control name="name" value={editUserForm.name} onChange={handleEditUserFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>E‑postadress</Form.Label>
              <Form.Control name="emailAddress" value={editUserForm.emailAddress} onChange={handleEditUserFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobilnummer</Form.Label>
              <Form.Control name="mobileNumber" value={editUserForm.mobileNumber} onChange={handleEditUserFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check type="checkbox" label="Meddela vid robotfel" name="notifyOnMowerError" checked={editUserForm.notifyOnMowerError} onChange={handleEditUserFormChange} />
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={closeEditUserModal} disabled={usersSaving}>Avbryt</Button>
              <Button variant="primary" type="submit" disabled={usersSaving}>
                {usersSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sparar...
                  </>
                ) : (
                  'Spara'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Login Modal */}
      <Modal show={showLoginModal} onHide={handleCloseLoginModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-lock"></i> Inloggning
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleLoginSubmit}>
            {loginError && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle"></i> {loginError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Användarnamn</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={loginData.username}
                onChange={handleLoginInputChange}
                placeholder="Ange användarnamn"
                disabled={loginLoading}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lösenord</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={loginData.password}
                onChange={handleLoginInputChange}
                placeholder="Ange lösenord"
                disabled={loginLoading}
                required
              />
            </Form.Group>
            <div className="d-grid">
              <Button variant="primary" type="submit" disabled={loginLoading}>
                {loginLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loggar in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i> Logga in
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Historik Modal */}
      <Modal show={showSettingsModal} onHide={handleCloseSettingsModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-clock-rotate-left"></i> Historik - {selectedMowerForSettings?.Name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Hämtar historik...
            </div>
          ) : historyError ? (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle"></i> {historyError}
            </Alert>
          ) : (
            <div>
              <div className="mb-3 d-flex align-items-center gap-2">
                <label htmlFor="timeIntervalSelect" className="form-label mb-0">Tidsintervall:</label>
                <select 
                  id="timeIntervalSelect"
                  className="form-select form-select-sm" 
                  value={selectedTimeInterval} 
                  onChange={handleTimeIntervalChange}
                  disabled={historyLoading}
                  style={{ maxWidth: '200px' }}
                >
                  <option value="30m">Senaste 30 minuterna</option>
                  <option value="3h">Senaste 3 timmarna</option>
                  <option value="12h">Senaste 12 timmarna</option>
                  <option value="24h">Senaste 24 timmarna</option>
                </select>
              </div>
              
              {historyData && historyData.success && historyData.items && historyData.items.length > 0 ? (
                <div>
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table className="table table-striped table-hover table-sm">
                      <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ width: '30px' }}></th>
                          <th>Tid</th>
                          <th>Status</th>
                          <th>Aktivitet</th>
                          <th>Batteri</th>
                          <th>Fel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.items
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((item, index) => (
                          <React.Fragment key={index}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => toggleRowExpansion(index)}>
                              <td>
                                <i 
                                  className={`fas fa-chevron-${expandedRows.has(index) ? 'down' : 'right'}`}
                                  style={{ color: '#666', fontSize: '12px' }}
                                ></i>
                              </td>
                              <td style={{ fontSize: '12px' }}>
                                {new Date(item.timestamp).toLocaleString('sv-SE')}
                              </td>
                              <td style={{ fontSize: '12px' }}>{item.status}</td>
                              <td style={{ fontSize: '12px' }}>{item.activity}</td>
                              <td style={{ fontSize: '12px' }}>
                                <span className={`badge ${
                                  item.batteryPercent >= 80 ? 'bg-success' :
                                  item.batteryPercent >= 50 ? 'bg-warning' : 'bg-danger'
                                }`}>
                                  {item.batteryPercent}%
                                </span>
                              </td>
                              <td style={{ fontSize: '12px' }}>
                                {item.hasError ? (
                                  <span className="badge bg-danger" title={item.errorMessage}>
                                    {item.errorCode}
                                  </span>
                                ) : (
                                  ''
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(index) && (
                              <tr>
                                <td colSpan="6" style={{ backgroundColor: '#f8f9fa', padding: '15px' }}>
                                  <pre style={{ 
                                    fontSize: '11px', 
                                    margin: 0, 
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    padding: '10px',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                  }}>
                                    {JSON.stringify(item, null, 2)}
                                  </pre>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : historyData ? (
                <Alert variant="info">
                  <i className="fas fa-info-circle"></i> Inga händelser hittades för det valda tidsintervallet.
                </Alert>
              ) : (
                <p>Ingen historik laddad.</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSettingsModal}>
            Stäng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Profile Modal */}
      <Modal show={showProfileModal} onHide={handleCloseProfileModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user"></i> Profilinställningar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {profileLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Hämtar profildata...
            </div>
          ) : profileError ? (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle"></i> {profileError}
            </Alert>
          ) : (
            <Form onSubmit={handleProfileFormSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Namn</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={profileFormData.name}
                  onChange={handleProfileFormChange}
                  placeholder="Ange ditt namn"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Epostadress</Form.Label>
                <Form.Control
                  type="email"
                  name="emailAddress"
                  value={profileFormData.emailAddress}
                  onChange={handleProfileFormChange}
                  placeholder="Ange epostadress"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Mobilnummer</Form.Label>
                <Form.Control
                  type="tel"
                  name="mobileNumber"
                  value={profileFormData.mobileNumber}
                  onChange={handleProfileFormChange}
                  placeholder="Ange mobilnummer"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <label className="ios-toggle-label">
                  <label className="ios-toggle">
                    <input
                  type="checkbox"
                  name="notifyOnMowerError"
                  checked={profileFormData.notifyOnMowerError}
                  onChange={handleProfileFormChange}
                />
                    <span className="ios-toggle-slider"></span>
                  </label>
                  <span>Meddela vid robotfel</span>
                </label>
              </Form.Group>
              
              <div className="d-flex gap-2 justify-content-end">
                <Button variant="secondary" onClick={handleCloseProfileModal} disabled={profileSaving}>
                  Avbryt
                </Button>
                <Button variant="primary" type="submit" size="sm" disabled={profileSaving}>
                  {profileSaving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sparar...
                    </>
                  ) : (
                    'Spara ändringar'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
      
      <div className="InfoContainer" style={{paddingTop: "15px"}}>
        <Container>
          <Row style={{paddingBottom: "15px"}}>
            <Col xs={12} sm={4} md={3} lg={3} className="text-center">
              <img src={Logo} alt="Logo" style={{width: "120px"}} />          
            </Col>
            <Col xs={12} sm={8} md={9} lg={9} >
              <h2>Klippschema 2026</h2>
              <p style={{"fontSize": "14px"}}>
                Tabellen visar årets veckoschema för Olsnäs Bollklubb. Vi justerar därför ansvaret i årets veckoschema från själva klippningen av gräsmattan, vilken nu Rolf tar hand om, till att istället efter behov inkludera kantklippning runt boden, boulebanan och borden samt plocka bort nedfallna trädkvistar och flytta på fotbollsmålen några meter (så att Rolf kommer åt oklippt yta runt ramen). Om någon vecka inte passar, ta kontakt med någon på listan och byt.
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
            <Accordion defaultActiveKey="1">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Förhållningsregler</Accordion.Header>
                <Accordion.Body>
                  <ul>
                    <li style={{"fontSize": "14px"}}>Veckans uppgifter omfattar efter behov kantklippning runt boden, boulebanan och borden.</li>
                    <li style={{"fontSize": "14px"}}>Plocka bort nedfallna trädkvistar.</li>
                    <li style={{"fontSize": "14px"}}>Flytta fotbollsmålen några meter så att Rolf kan klippa oklippt yta runt målramen.</li>
                    <li style={{"fontSize": "14px"}}>Själva klippningen av gräsmattan sköter Rolf.</li>
                    <li style={{"fontSize": "14px"}}>Får man förhinder någon vecka kontaktar man någon annan på listan och byter vecka.</li>
                    <li style={{"fontSize": "14px"}}>Kodlås till boden finns för enkel tillgång till verktyg och utrustning.</li>
                  </ul>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            </Col>
          </Row>          
          <Row>
            <Col>
              { true && <WeeklySchedule /> }
            </Col>
          </Row>          
        </Container>
      </div>
      <div style={{height: "15px"}}></div>
      
      {/* Success Toast/Snackbar */}
      <Toast 
        show={showSuccessToast} 
        onClose={() => setShowSuccessToast(false)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none'
        }}
        autohide
        delay={2000}
      >
        <Toast.Body style={{ fontWeight: 'bold' }}>
          <i className="fas fa-check-circle me-2"></i>
          {successToastMessage || 'Klart'}
        </Toast.Body>
      </Toast>
   </div>
  );
}

export default App;
